import React, { useEffect, useMemo, useState } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import { useNavigate } from 'react-router-dom';
import styles from './CreateNewUser.module.css';
import { signup } from '../api/authApi';
import AppLayout from '../components/AppLayout';

const translations = {
  en: {
    pageTitle: 'Create New User',
    introTitle: 'Create access in minutes',
    introSubtitle:
      'Provision secure accounts, assign roles, and keep your teams moving with confidence.',
    formHeading: 'User details',
    username: 'Username',
    password: 'Password',
    userType: 'User type',
    enterUsername: 'Enter username',
    enterPassword: 'Enter password',
    passwordHint: 'Use at least 8 characters that combine letters and numbers.',
    createUser: 'Create user',
    cancel: 'Cancel',
    reset: 'Reset form',
    successTitle: 'Success',
    errorTitle: 'Error',
    successMessage: 'User created successfully!',
    errorMessage: 'An error occurred. Please try again.',
    close: 'Close',
    homepage: 'Homepage',
    introPoints: [
      'Assign clear roles and permissions.',
      'Keep teams informed with instant access.',
      'Audit every change through system logs.',
    ],
  },
  ar: {
    pageTitle: 'إنشاء مستخدم جديد',
    introTitle: 'أنشئ حسابًا في دقائق',
    introSubtitle:
      'وفّر الوصول الآمن، وخصص الصلاحيات، وحافظ على سير العمل بسلاسة.',
    formHeading: 'تفاصيل المستخدم',
    username: 'اسم المستخدم',
    password: 'كلمة المرور',
    userType: 'نوع المستخدم',
    enterUsername: 'أدخل اسم المستخدم',
    enterPassword: 'أدخل كلمة المرور',
    passwordHint: 'استخدم 8 أحرف على الأقل مع أرقام وحروف.',
    createUser: 'إنشاء مستخدم',
    cancel: 'إلغاء',
    reset: 'إعادة التعيين',
    successTitle: 'نجاح',
    errorTitle: 'خطأ',
    successMessage: 'تم إنشاء المستخدم بنجاح!',
    errorMessage: 'حدث خطأ. حاول مرة أخرى.',
    close: 'إغلاق',
    homepage: 'الصفحة الرئيسية',
    introPoints: [
      'حدّد الصلاحيات والمسؤوليات بوضوح.',
      'أوصل الفرق فورًا بالمساحات التي يحتاجونها.',
      'تابع كل تغيير من خلال سجلات النظام.',
    ],
  },
};

const userTypes = ['admin', 'manager', 'sales-manager', 'accounting', 'sales', 'worker'];

const CreateNewUser = () => {
  const navigate = useNavigate();
  const [language, setLanguage] = usePersistentLanguage();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState('worker');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          userType: payload.userType,
          requesterUsername: payload.username,
        };
      } catch (error) {
        console.error('Error parsing token payload:', error);
        return { userType: null, requesterUsername: null };
      }
    }
    return { userType: null, requesterUsername: null };
  }, [token]);

  const dictionary = translations[language];
  const t = (key) => dictionary[key];
  const availableUserTypes = useMemo(
    () =>
      userInfo.userType === 'sales-manager'
        ? ['sales-manager', 'sales']
        : userTypes,
    [userInfo.userType]
  );

  useEffect(() => {
    if (!availableUserTypes.includes(userType)) {
      setUserType(availableUserTypes[0] || 'worker');
    }
  }, [availableUserTypes, userType]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      await signup(username.trim(), password, userType);
      setModalMessage({ type: 'success', text: t('successMessage') });
    } catch (error) {
      if (error?.response?.data?.error) {
        setModalMessage({ type: 'error', text: error.response.data.error });
      } else {
        setModalMessage({ type: 'error', text: t('errorMessage') });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalMessage(null);
    if (modalMessage?.type === 'success') {
      setUsername('');
      setPassword('');
      setUserType(availableUserTypes[0] || 'worker');
    }
  };

  const handleLanguageToggle = () => {
    setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'));
  };

  const handleReset = () => {
    setUsername('');
    setPassword('');
    setUserType(availableUserTypes[0] || 'worker');
  };

  return (
    <AppLayout
      title={t('pageTitle')}
      language={language}
      onToggleLanguage={handleLanguageToggle}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.page}
      contentProps={{ 'aria-busy': loading }}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
        </div>
      )}

      <section className={styles.content}>
        <aside className={styles.introCard}>
          <h2>{t('introTitle')}</h2>
          <p>{t('introSubtitle')}</p>
          <ul>
            {dictionary.introPoints?.map((point, index) => (
              <li key={`intro-point-${index}`}>{point}</li>
            ))}
          </ul>
        </aside>

        <form className={styles.formCard} onSubmit={handleSubmit}>
          <h3>{t('formHeading')}</h3>
          <div className={styles.formGrid}>
            <label className={styles.inputField}>
              <span>{t('username')}</span>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('enterUsername')}
                required
                disabled={loading}
              />
            </label>

            <label className={styles.inputField}>
              <span>{t('password')}</span>
              <div className={styles.passwordField}>
                <input
                  type={passwordVisible ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('enterPassword')}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible((prev) => !prev)}
                  className={styles.togglePassword}
                >
                  {passwordVisible ? 'Hide' : 'Show'}
                </button>
              </div>
              <small>{t('passwordHint')}</small>
            </label>

            <label className={styles.inputField}>
              <span>{t('userType')}</span>
              <select
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                disabled={loading}
              >
                {availableUserTypes.map((type) => {
                  let labelEn = '';
                  let labelAr = '';
                  switch (type) {
                    case 'admin':
                      labelEn = 'Admin';
                      labelAr = 'مدير';
                      break;
                    case 'manager':
                      labelEn = 'Factory Manager';
                      labelAr = 'مشرف مصنع';
                      break;
                    case 'sales-manager':
                      labelEn = 'Sales Manager';
                      labelAr = 'مدير المبيعات';
                      break;
                    case 'accounting':
                      labelEn = 'Accounting';
                      labelAr = 'المحاسبة';
                      break;
                    case 'sales':
                      labelEn = 'Sales';
                      labelAr = 'مبيعات';
                      break;
                    default:
                      labelEn = 'Worker';
                      labelAr = 'عامل';
                  }

                  return (
                    <option key={type} value={type}>
                      {language === 'en' ? labelEn : labelAr}
                    </option>
                  );
                })}
              </select>
            </label>
          </div>

          <div className={styles.formActions}>
            <button type="submit" className={styles.primaryButton} disabled={loading}>
              {t('createUser')}
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => navigate(-1)}
              disabled={loading}
            >
              {t('cancel')}
            </button>
            <button type="button" className={styles.linkButton} onClick={handleReset} disabled={loading}>
              {t('reset')}
            </button>
          </div>
        </form>
      </section>

      {modalMessage && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalCard}>
            <h3>{modalMessage.type === 'success' ? t('successTitle') : t('errorTitle')}</h3>
            <p>{modalMessage.text}</p>
            <button type="button" onClick={handleCloseModal} className={styles.primaryButton}>
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default CreateNewUser;
